<template>
  <div class="gear-search-select">
    <b-form-group :label="label">
      <b-input-group>
        <b-form-input
          v-model="searchTerm"
          :disabled="disabled"
          :placeholder="placeholder"
        />
        <b-input-group-append v-if="searchTerm">
          <b-button
            variant="outline-secondary"
            :disabled="disabled || loading"
            @click="clearSearch"
          >
            Effacer
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <div class="gear-search-results mb-3">
      <div v-if="showEmptyHint" class="text-muted small py-2">
        {{ emptyHint }}
      </div>

      <template v-else>
        <div v-if="loading" class="text-center py-3">
          <b-spinner small />
          <div class="text-muted mt-2">Chargement du materiel...</div>
        </div>
        <b-alert v-else-if="error" show variant="danger" class="border">
          {{ error }}
        </b-alert>

        <b-list-group v-else-if="showResults">
          <b-list-group-item
            v-for="item in filteredItems"
            :key="item.id || item._id || item.full_name || item.name"
            :button="item.usable !== false"
            :disabled="item.usable === false"
            :active="isSelected(item)"
            class="d-flex justify-content-between align-items-center"
            :class="{ 'gear-item-unusable': item.usable === false }"
            @click="selectItem(item)"
          >
            <div class="flex-grow-1 mr-2">
              <div class="font-weight-bold">
                {{ getPrimaryLabel(item) }}
                <b-badge
                  v-if="item.usable === false"
                  variant="danger"
                  pill
                  class="ml-2"
                >
                  Inutilisable
                </b-badge>
              </div>
              <small v-if="getSecondaryLabel(item)" class="text-muted d-block">
                {{ getSecondaryLabel(item) }}
              </small>
              <small v-if="item.comments" class="text-muted d-block font-italic">
                {{ item.comments }}
              </small>
            </div>
            <b-badge
              v-if="type === 'sails'"
              :variant="item.rigged ? 'success' : 'secondary'"
              pill
            >
              {{ item.rigged ? 'Gréée' : 'Non gréée' }}
            </b-badge>
          </b-list-group-item>
        </b-list-group>

        <b-alert v-else show variant="light" class="border text-muted">
          {{ noResultsText }}
        </b-alert>
      </template>
    </div>
  </div>
</template>

<script>
import GearService from "@/services/gear.service";
import debounce from "@/utils/debounce";

export default {
  name: "GearSearchSelect",
  props: {
    value: {
      type: [Object, String],
      default: null
    },
    type: {
      type: String,
      default: "sails"
    },
    emptyHint: {
      type: String,
      default: "Commencez a taper pour rechercher du materiel."
    },
    limit: {
      type: Number,
      default: 25
    },
    label: {
      type: String,
      default: "Recherche"
    },
    placeholder: {
      type: String,
      default: "Nom, taille ou reference"
    },
    noResultsText: {
      type: String,
      default: "Aucun materiel ne correspond a la recherche actuelle."
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchTerm: "",
      items: [],
      loading: false,
      error: null,
      itemsLoaded: false,
      searchDebounced: null
    };
  },
  computed: {
    hasSearchTerm() {
      return this.searchTerm.trim().length > 0;
    },
    filteredItems() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return [];
      const matches = this.items.filter(item =>
        this.getSearchableText(item).includes(term)
      );
      return matches.slice(0, this.limit);
    },
    showEmptyHint() {
      return !this.hasSearchTerm;
    },
    showResults() {
      return this.hasSearchTerm && !this.loading && this.filteredItems.length;
    }
  },
  watch: {
    searchTerm() {
      this.handleSearchTermChange();
    }
  },
  created() {
    this.searchDebounced = debounce(() => {
      this.ensureItemsLoaded();
    }, 300);
  },
  beforeDestroy() {
    if (this.searchDebounced) {
      this.searchDebounced.cancel();
    }
  },
  methods: {
    getItemLabel(item) {
      if (!item) return "";
      return item.full_name || item.name || item.label || "";
    },
    getPrimaryLabel(item) {
      if (!item) return "";
      const fullName = item.full_name || item.name || item.label || "";
      if (this.type === "sails") {
        const parts = [];
        if (item.guc_id) parts.push(item.guc_id);
        if (fullName) parts.push(fullName);
        return parts.join(" · ");
      }
      const parts = [];
      if (item.brand) parts.push(item.brand);
      if (fullName) parts.push(fullName);
      return parts.join(" · ");
    },
    getSecondaryLabel(item) {
      if (!item) return "";
      if (this.type === "sails") {
        const parts = [];
        if (item.brand) parts.push(item.brand);
        if (typeof item.size === "number") parts.push(`${item.size} m²`);
        return parts.join(" · ");
      }
      const parts = [];
      if (typeof item.volume === "number") parts.push(`${item.volume} L`);
      return parts.join(" · ");
    },
    getSearchableText(item) {
      if (!item) return "";
      const fullName = item.full_name || item.name || item.label || "";
      const tokens = [
        item.guc_id,
        item.brand,
        fullName,
        item.comments,
        typeof item.size === "number" ? String(item.size) : null,
        typeof item.volume === "number" ? String(item.volume) : null
      ];
      return tokens.filter(Boolean).join(" ").toLowerCase();
    },
    isSelected(item) {
      if (!this.value) return false;
      if (typeof this.value === "string") {
        return this.getItemLabel(item) === this.value;
      }
      const currentId = this.value.id || this.value._id || this.value.full_name || this.value.name;
      const itemId = item.id || item._id || item.full_name || item.name;
      return currentId && itemId ? currentId === itemId : false;
    },
    async ensureItemsLoaded() {
      if (this.itemsLoaded || !this.hasSearchTerm) return;
      this.loading = true;
      this.error = null;
      try {
        const response =
          this.type === "boards"
            ? await GearService.listAllBoards()
            : await GearService.listAllSails();
        const data = response && response.data ? response.data : response;
        this.items = Array.isArray(data) ? data : [];
        this.itemsLoaded = true;
      } catch (err) {
        this.error = err.message || "Erreur inattendue lors du chargement du materiel.";
      } finally {
        this.loading = false;
      }
    },
    handleSearchTermChange() {
      if (!this.searchDebounced) return;
      this.searchDebounced();
    },
    selectItem(item) {
      if (!item) return;
      if (item.usable === false) return;
      this.$emit("input", item);
      this.$emit("selected", item);
      this.clearSearch();
    },
    clearSelection() {
      this.$emit("input", null);
      this.$emit("cleared");
    },
    clearSearch() {
      this.searchTerm = "";
    },
    reset() {
      this.searchTerm = "";
      this.clearSelection();
    }
  }
};
</script>

<style scoped>
.gear-item-unusable {
  background-color: #f8d7da;
  color: #842029;
  cursor: not-allowed;
  opacity: 0.75;
}
</style>
